import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  apiClient,
  authHeaders,
  getToken,
  setToken,
  clearToken,
  photoImageUrl,
} from "../lib/api";
import {
  Upload,
  Trash2,
  ArrowLeft,
  LogOut,
  Plus,
  Pencil,
  Image as ImageIcon,
  Camera,
  FileText,
  X,
  Check,
  GripVertical,
  RefreshCw,
  Facebook,
} from "lucide-react";

export default function AdminPage() {
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);
  const [password, setPassword] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);

  useEffect(() => {
    const t = getToken();
    if (!t) {
      setChecking(false);
      return;
    }
    apiClient
      .post("/admin/verify", null, { headers: authHeaders() })
      .then(() => setAuthed(true))
      .catch(() => clearToken())
      .finally(() => setChecking(false));
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    try {
      const res = await apiClient.post("/admin/login", { password });
      setToken(res.data.token);
      setAuthed(true);
      toast.success("Welcome back.");
    } catch (err) {
      toast.error("Incorrect passkey.");
    } finally {
      setLoggingIn(false);
    }
  };

  const handleLogout = () => {
    clearToken();
    setAuthed(false);
    setPassword("");
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center text-white/50">
        Loading…
      </div>
    );
  }

  if (!authed) {
    return (
      <div
        className="min-h-screen flex items-center justify-center px-6"
        data-testid="admin-login"
      >
        <form
          onSubmit={handleLogin}
          className="glass-strong rounded-2xl p-10 w-full max-w-md"
        >
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-white/60 hover:text-yellow-400 text-sm mb-6 transition-colors"
            data-testid="admin-back-home"
          >
            <ArrowLeft size={16} /> Back to site
          </Link>
          <h1 className="text-4xl font-light text-white mb-2 tracking-tight">
            Admin Portal
          </h1>
          <p className="text-white/50 text-sm mb-8">
            Enter your passkey to manage site content.
          </p>
          <input
            type="password"
            placeholder="PASSKEY"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full bg-white/5 border border-white/15 rounded-xl px-5 py-4 text-white placeholder:text-white/40 placeholder:text-sm placeholder:tracking-[0.3em] focus:outline-none focus:border-yellow-400/50 transition-colors mb-4 text-center text-lg tracking-[0.3em]"
            data-testid="admin-password-input"
            autoFocus
          />
          <button
            type="submit"
            disabled={loggingIn}
            className="w-full btn-accent rounded-xl px-7 py-4 text-sm tracking-[0.2em] disabled:opacity-60"
            data-testid="admin-login-submit"
          >
            {loggingIn ? "VERIFYING…" : "UNLOCK"}
          </button>
        </form>
      </div>
    );
  }

  return <AdminDashboard onLogout={handleLogout} />;
}

function AdminDashboard({ onLogout }) {
  const [tab, setTab] = useState("photos");

  return (
    <div className="min-h-screen px-5 md:px-8 py-8 md:py-10" data-testid="admin-dashboard">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-10">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-white/60 hover:text-yellow-400 text-sm transition-colors"
            data-testid="admin-back-home"
          >
            <ArrowLeft size={16} /> Back to site
          </Link>
          <button
            onClick={onLogout}
            className="inline-flex items-center gap-2 text-white/60 hover:text-yellow-400 text-sm transition-colors"
            data-testid="admin-logout"
          >
            <LogOut size={16} /> Log out
          </button>
        </div>

        <div className="mb-10">
          <h1 className="text-4xl md:text-5xl font-light text-white tracking-tight mb-2">
            Site Manager
          </h1>
          <p className="text-white/60">
            Manage photos, statements, and site content.
          </p>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-10 border-b border-white/10" data-testid="admin-tabs">
          <TabButton
            active={tab === "photos"}
            onClick={() => setTab("photos")}
            icon={<Camera size={16} />}
            label="Photos"
            testid="admin-tab-photos"
          />
          <TabButton
            active={tab === "statements"}
            onClick={() => setTab("statements")}
            icon={<FileText size={16} />}
            label="Statements"
            testid="admin-tab-statements"
          />
        </div>

        {tab === "photos" && <PhotosManager />}
        {tab === "statements" && <StatementsManager />}
      </div>
    </div>
  );
}

const TabButton = ({ active, onClick, icon, label, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`inline-flex items-center gap-2 px-5 py-3 text-sm uppercase tracking-[0.18em] border-b-2 transition-colors -mb-px ${
      active
        ? "border-yellow-400 text-yellow-400"
        : "border-transparent text-white/60 hover:text-white"
    }`}
  >
    {icon} {label}
  </button>
);

/* ===================== PHOTOS MANAGER ===================== */
function PhotosManager() {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get("/photos");
      setPhotos(res.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const uploadFiles = async (files) => {
    const list = Array.from(files);
    if (list.length === 0) return;
    setUploading(true);
    let success = 0;
    for (const file of list) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("caption", "");
        await apiClient.post("/photos", fd, {
          headers: {
            ...authHeaders(),
            "Content-Type": "multipart/form-data",
          },
        });
        success += 1;
      } catch (err) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    setUploading(false);
    if (success > 0) {
      toast.success(`${success} photo${success > 1 ? "s" : ""} uploaded.`);
      load();
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files) uploadFiles(e.dataTransfer.files);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this photo?")) return;
    try {
      await apiClient.delete(`/photos/${id}`, { headers: authHeaders() });
      toast.success("Photo deleted.");
      setPhotos((p) => p.filter((x) => x.id !== id));
    } catch (err) {
      toast.error("Could not delete photo.");
    }
  };

  return (
    <div data-testid="admin-photos-panel">
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        className={`glass-strong rounded-2xl p-8 md:p-14 text-center cursor-pointer transition-all ${
          dragOver ? "border-yellow-400/60 bg-yellow-400/5" : ""
        }`}
        data-testid="admin-dropzone"
      >
        <Upload className="mx-auto mb-4 text-white/60" size={36} />
        <p className="text-white font-medium mb-1">
          {uploading ? "Uploading…" : "Drop photos here"}
        </p>
        <p className="text-white/50 text-sm">
          or click to choose files (JPG, PNG, WebP)
        </p>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept="image/*"
          className="hidden"
          onChange={(e) => uploadFiles(e.target.files)}
          data-testid="admin-file-input"
        />
      </div>

      <div className="mt-12">
        <h2 className="text-lg text-white/80 mb-5 tracking-wide">
          Gallery ({photos.length})
        </h2>
        {loading ? (
          <p className="text-white/40">Loading…</p>
        ) : photos.length === 0 ? (
          <p className="text-white/40">No photos yet.</p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {photos.map((p) => (
              <div
                key={p.id}
                className="relative group rounded-xl overflow-hidden glass"
                data-testid={`admin-photo-${p.id}`}
              >
                <img
                  src={photoImageUrl(p.id)}
                  alt={p.caption || "Sunset"}
                  className="w-full h-40 object-cover"
                />
                <button
                  onClick={() => handleDelete(p.id)}
                  className="absolute top-2 right-2 bg-black/70 hover:bg-red-500 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                  aria-label="Delete"
                  data-testid={`admin-delete-${p.id}`}
                >
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ===================== STATEMENTS MANAGER ===================== */
function StatementsManager() {
  const [statements, setStatements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // statement or "new"
  const [dragId, setDragId] = useState(null);
  const [overId, setOverId] = useState(null);
  const [savingOrder, setSavingOrder] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const fbDefault = "https://m.facebook.com/Councilmanwood/";

  const load = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get("/statements");
      setStatements(res.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this statement?")) return;
    try {
      await apiClient.delete(`/statements/${id}`, { headers: authHeaders() });
      toast.success("Statement deleted.");
      load();
    } catch (err) {
      toast.error("Could not delete.");
    }
  };

  const syncFacebook = async () => {
    setSyncing(true);
    try {
      const res = await apiClient.post("/admin/sync-facebook", null, {
        headers: authHeaders(),
      });
      const { synced, error, skipped, note, method } = res.data;
      if (error) {
        toast.error(`Sync failed: ${error}`);
      } else if (synced > 0) {
        toast.success(
          `Imported ${synced} new statement${synced !== 1 ? "s" : ""} from Facebook.`
        );
        load();
      } else if (note) {
        // e.g. public page behind a login wall
        toast.error(
          `No posts imported — ${note}. Paste an rss.app feed URL above to connect Facebook.`
        );
      } else if (skipped) {
        toast.error("Facebook sync is not configured on the server.");
      } else {
        toast.success(
          `Facebook is up to date${method ? ` (via ${method})` : ""}.`
        );
      }
    } catch {
      toast.error("Sync request failed. Check your network connection.");
    } finally {
      setSyncing(false);
    }
  };

  const persistOrder = async (ordered) => {
    setSavingOrder(true);
    try {
      await apiClient.post(
        "/statements/reorder",
        { order: ordered.map((s) => s.id) },
        { headers: authHeaders() }
      );
      toast.success("Order saved.");
    } catch (err) {
      toast.error("Could not save order.");
      load();
    } finally {
      setSavingOrder(false);
    }
  };

  const handleDragStart = (id) => (e) => {
    setDragId(id);
    e.dataTransfer.effectAllowed = "move";
    // Required in Firefox to enable drag
    try { e.dataTransfer.setData("text/plain", id); } catch (_) {}
  };

  const handleDragOver = (id) => (e) => {
    e.preventDefault();
    if (id !== overId) setOverId(id);
  };

  const handleDrop = (targetId) => (e) => {
    e.preventDefault();
    if (!dragId || dragId === targetId) {
      setDragId(null);
      setOverId(null);
      return;
    }
    const next = [...statements];
    const from = next.findIndex((s) => s.id === dragId);
    const to = next.findIndex((s) => s.id === targetId);
    if (from === -1 || to === -1) return;
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setStatements(next);
    setDragId(null);
    setOverId(null);
    persistOrder(next);
  };

  const handleDragEnd = () => {
    setDragId(null);
    setOverId(null);
  };

  return (
    <div data-testid="admin-statements-panel">
      <FacebookFeedSettings onImported={load} />
      <div className="flex flex-wrap justify-between items-center gap-3 mb-6">
        <p className="text-white/60 text-sm">
          {statements.length} statement{statements.length !== 1 ? "s" : ""}
          {savingOrder && (
            <span className="ml-3 text-yellow-400/80 text-xs uppercase tracking-wider">
              saving order…
            </span>
          )}
        </p>
        <div className="flex items-center gap-2">
          <button
            onClick={syncFacebook}
            disabled={syncing}
            title="Import new posts from the Facebook page"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full glass border border-white/15 text-sm text-white/70 hover:text-yellow-400 hover:border-yellow-400/40 transition-colors disabled:opacity-50"
            data-testid="admin-sync-facebook"
          >
            {syncing ? (
              <RefreshCw size={14} className="animate-spin" />
            ) : (
              <Facebook size={14} />
            )}
            {syncing ? "SYNCING…" : "SYNC FACEBOOK"}
          </button>
          <button
            onClick={() =>
              setEditing({
                id: null,
                title: "",
                body: "",
                date_label: "",
                facebook_url: fbDefault,
              })
            }
            className="btn-accent inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm"
            data-testid="admin-new-statement"
          >
            <Plus size={16} /> NEW STATEMENT
          </button>
        </div>
      </div>

      {!loading && statements.length > 1 && (
        <p className="text-white/40 text-xs uppercase tracking-[0.22em] mb-3">
          Drag the handle to reorder
        </p>
      )}

      {loading ? (
        <p className="text-white/40">Loading…</p>
      ) : (
        <div className="space-y-3" data-testid="admin-statements-list">
          {statements.map((s) => (
            <div
              key={s.id}
              draggable
              onDragStart={handleDragStart(s.id)}
              onDragOver={handleDragOver(s.id)}
              onDrop={handleDrop(s.id)}
              onDragEnd={handleDragEnd}
              className={`glass rounded-xl p-4 md:p-5 flex items-start gap-3 md:gap-4 transition-all ${
                dragId === s.id ? "opacity-40 scale-[0.99]" : ""
              } ${
                overId === s.id && dragId !== s.id
                  ? "ring-2 ring-yellow-400/60 translate-y-[1px]"
                  : ""
              }`}
              data-testid={`admin-statement-${s.id}`}
            >
              <button
                type="button"
                className="self-stretch flex items-center justify-center px-1 text-white/30 hover:text-yellow-400 cursor-grab active:cursor-grabbing"
                aria-label="Drag to reorder"
                data-testid={`admin-drag-${s.id}`}
                onMouseDown={(e) => e.currentTarget.parentElement?.setAttribute("draggable", "true")}
              >
                <GripVertical size={18} />
              </button>
              {s.image_id && (
                <img
                  src={photoImageUrl(s.image_id)}
                  alt=""
                  className="w-16 h-16 object-cover rounded-lg border border-white/10 flex-shrink-0"
                />
              )}
              <div className="flex-1 min-w-0">
                <p className="text-yellow-400/80 text-xs uppercase tracking-wider mb-1">
                  {s.date_label || "—"}
                </p>
                <h3 className="text-white font-medium truncate">{s.title}</h3>
                <p className="text-white/50 text-sm mt-1 line-clamp-2">
                  {s.body}
                </p>
              </div>
              <div className="flex gap-2 flex-shrink-0">
                <button
                  onClick={() => setEditing(s)}
                  className="p-2 rounded-lg bg-white/5 hover:bg-yellow-400/20 text-white/80 hover:text-yellow-400 transition-colors"
                  aria-label="Edit"
                  data-testid={`admin-edit-statement-${s.id}`}
                >
                  <Pencil size={16} />
                </button>
                <button
                  onClick={() => remove(s.id)}
                  className="p-2 rounded-lg bg-white/5 hover:bg-red-500/30 text-white/80 hover:text-red-400 transition-colors"
                  aria-label="Delete"
                  data-testid={`admin-delete-statement-${s.id}`}
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <StatementEditor
          statement={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      )}
    </div>
  );
}

function StatementEditor({ statement, onClose, onSaved }) {
  const isNew = !statement.id;
  const [form, setForm] = useState({
    title: statement.title || "",
    body: statement.body || "",
    date_label: statement.date_label || "",
    facebook_url: statement.facebook_url || "",
  });
  const [imageFile, setImageFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    if (!form.title || !form.body) {
      toast.error("Title and body are required.");
      return;
    }
    setSaving(true);
    try {
      let id = statement.id;
      if (isNew) {
        const res = await apiClient.post("/statements", form, {
          headers: authHeaders(),
        });
        id = res.data.id;
      } else {
        await apiClient.patch(`/statements/${id}`, form, {
          headers: authHeaders(),
        });
      }
      if (imageFile && id) {
        const fd = new FormData();
        fd.append("file", imageFile);
        await apiClient.post(`/statements/${id}/image`, fd, {
          headers: {
            ...authHeaders(),
            "Content-Type": "multipart/form-data",
          },
        });
      }
      toast.success(isNew ? "Statement created." : "Statement updated.");
      onSaved();
    } catch (err) {
      toast.error("Could not save.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-md flex items-center justify-center p-4 md:p-8"
      onClick={onClose}
      data-testid="statement-editor-modal"
    >
      <form
        onSubmit={save}
        onClick={(e) => e.stopPropagation()}
        className="glass-strong rounded-2xl p-6 md:p-8 w-full max-w-2xl max-h-[92vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-light text-white">
            {isNew ? "New Statement" : "Edit Statement"}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-white/60 hover:text-white"
            data-testid="statement-editor-close"
          >
            <X size={22} />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="text-xs uppercase tracking-[0.18em] text-white/50 mb-2 block">
              Title
            </label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="w-full bg-white/5 border border-white/15 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-yellow-400/50"
              data-testid="statement-input-title"
            />
          </div>

          <div>
            <label className="text-xs uppercase tracking-[0.18em] text-white/50 mb-2 block">
              Date Label (e.g. "About 2 months ago")
            </label>
            <input
              type="text"
              value={form.date_label}
              onChange={(e) =>
                setForm({ ...form, date_label: e.target.value })
              }
              className="w-full bg-white/5 border border-white/15 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-yellow-400/50"
              data-testid="statement-input-date"
            />
          </div>

          <div>
            <label className="text-xs uppercase tracking-[0.18em] text-white/50 mb-2 block">
              Body
            </label>
            <textarea
              rows={10}
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              className="w-full bg-white/5 border border-white/15 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-yellow-400/50 resize-none"
              data-testid="statement-input-body"
            />
          </div>

          <div>
            <label className="text-xs uppercase tracking-[0.18em] text-white/50 mb-2 block">
              Facebook URL (optional)
            </label>
            <input
              type="url"
              value={form.facebook_url}
              onChange={(e) =>
                setForm({ ...form, facebook_url: e.target.value })
              }
              placeholder="https://m.facebook.com/Councilmanwood/"
              className="w-full bg-white/5 border border-white/15 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-yellow-400/50"
              data-testid="statement-input-fb"
            />
          </div>

          <div>
            <label className="text-xs uppercase tracking-[0.18em] text-white/50 mb-2 block">
              Attach Image (optional)
            </label>
            <label className="flex items-center gap-3 glass rounded-xl px-4 py-3 cursor-pointer hover:border-yellow-400/30 transition-colors">
              <ImageIcon size={18} className="text-yellow-400" />
              <span className="text-white/70 text-sm flex-1 truncate">
                {imageFile
                  ? imageFile.name
                  : statement.image_id
                  ? "Image attached (upload new to replace)"
                  : "Choose image"}
              </span>
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) =>
                  setImageFile(e.target.files?.[0] || null)
                }
                data-testid="statement-input-image"
              />
            </label>
          </div>
        </div>

        <div className="flex gap-3 mt-7">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 btn-ghost rounded-xl px-5 py-3 text-sm uppercase tracking-wider"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="flex-1 btn-accent rounded-xl px-5 py-3 text-sm tracking-wider inline-flex items-center justify-center gap-2 disabled:opacity-60"
            data-testid="statement-save"
          >
            <Check size={16} /> {saving ? "SAVING…" : "SAVE"}
          </button>
        </div>
      </form>
    </div>
  );
}

/* Paste an rss.app (or fetchrss) feed URL built from the public Facebook page.
   Once saved, every new Facebook post becomes a statement automatically
   (the server re-checks every 30 minutes). */
const FacebookFeedSettings = ({ onImported }) => {
  const [feedUrl, setFeedUrl] = useState("");
  const [method, setMethod] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    apiClient
      .get("/admin/facebook-settings", { headers: authHeaders() })
      .then((res) => {
        setFeedUrl(res.data.feed_url || "");
        setMethod(res.data.method);
      })
      .catch(() => {});
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await apiClient.put(
        "/admin/facebook-settings",
        { feed_url: feedUrl },
        { headers: authHeaders() }
      );
      const { synced = 0, error } = res.data;
      setMethod(feedUrl.trim() ? "feed" : "scrape");
      if (error) toast.error(`Saved, but the feed could not be read: ${error}`);
      else if (synced > 0) {
        toast.success(`Saved. Imported ${synced} post${synced !== 1 ? "s" : ""} from Facebook.`);
        onImported();
      } else toast.success("Saved. Facebook is up to date.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save feed URL.");
    } finally {
      setSaving(false);
    }
  };

  const connected = method === "feed" || method === "graph";

  return (
    <form
      onSubmit={save}
      className="glass rounded-2xl p-5 md:p-6 mb-8"
      data-testid="admin-fb-feed"
    >
      <div className="flex items-center justify-between gap-3 mb-2">
        <p className="text-white text-sm font-medium inline-flex items-center gap-2">
          <Facebook size={15} /> Facebook → Statements
        </p>
        <span
          className={`text-[11px] px-2.5 py-1 rounded-full ${
            connected ? "bg-emerald-400/15 text-emerald-300" : "bg-white/10 text-white/60"
          }`}
        >
          {method === "graph" ? "Connected (Graph API)" : connected ? "Connected" : "Not connected"}
        </span>
      </div>
      <p className="text-white/55 text-xs leading-relaxed mb-4">
        Go to rss.app, paste <span className="text-white/80">facebook.com/Councilmanwood</span>,
        create the feed, and paste the feed URL here. Every Facebook post then
        appears in Statements automatically (checked every 30 minutes).
      </p>
      <div className="flex flex-col sm:flex-row gap-2">
        <input
          type="url"
          value={feedUrl}
          onChange={(e) => setFeedUrl(e.target.value)}
          placeholder="https://rss.app/feeds/xxxx.xml"
          className="press-input flex-1"
          data-testid="admin-fb-feed-input"
        />
        <button
          type="submit"
          disabled={saving}
          className="btn-accent px-5 py-2.5 rounded-full text-sm disabled:opacity-60"
          data-testid="admin-fb-feed-save"
        >
          {saving ? "Saving…" : "Save & Import"}
        </button>
      </div>
    </form>
  );
};
